import type { ReactNode, Ref } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { TitlePlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { SignalId, SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { SignalUpsellBanner } from "app/pages/sales-intelligence/modules/signals/components/SignalUpsellBanner/SignalUpsellBanner";
import { getIconForSignalType } from "app/pages/sales-intelligence/modules/signals/constants/signalTypeIcons";
import { useSignalAccessRequestSent } from "app/pages/sales-intelligence/modules/signals/hooks/useSignalAccessRequestSent";
import AddSignalButton from "app/pages/sales-intelligence/sub-modules/signals/components/buttons/AddSignalButton/AddSignalButton";
import SignalsListItems from "app/pages/sales-intelligence/sub-modules/signals/components/views/SignalsManagementView/SignalsListItems";
import type SignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalFilters";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";

import { AccountSignalTypeIcon } from "../AccountSignalTypeIcon/AccountSignalTypeIcon";
import { AccountSignalTypeSectionEmptyState } from "../AccountSignalTypeSectionEmptyState/AccountSignalTypeSectionEmptyState";

import { StyledAccountSignalTypeSection } from "./styles";

type AccountSignalTypeSectionProps = {
    signalType: SignalType;
    icon: IconName;
    signals: readonly UISignal<SignalFilters>[];
    highlightedSignalId?: SignalId | null;
    onSignalCreate(signalType: SignalType): void;
    onSignalDelete<F extends SignalFilters>(signal: Readonly<UISignal<F>>): void;
    onSignalEdit<F extends SignalFilters>(signal: Readonly<UISignal<F>>): void;
    areActionsEnabled?: boolean;
    isLocked?: boolean;
    onRequestAccess?: VoidFunction;
    showsRequestSentState?: boolean;
    isLoading?: boolean;
    sectionRef?: Ref<HTMLDivElement>;
    headerAction?: ReactNode;
    meta?: ReactNode;
};

export const AccountSignalTypeSection = ({
    signalType,
    icon,
    signals,
    highlightedSignalId,
    onSignalCreate,
    onSignalDelete,
    onSignalEdit,
    areActionsEnabled = true,
    isLocked = false,
    onRequestAccess,
    showsRequestSentState = false,
    isLoading = false,
    sectionRef,
    headerAction,
    meta,
}: AccountSignalTypeSectionProps) => {
    const { translate } = useTranslate();
    const [isAccessRequestSent, markAccessRequestSent] = useSignalAccessRequestSent(signalType);

    const handleRequestAccess = (): void => {
        onRequestAccess?.();
        if (showsRequestSentState) {
            markAccessRequestSent();
        }
    };

    const handleAddSignalClick = (): void => {
        onSignalCreate(signalType);
    };

    return (
        <StyledAccountSignalTypeSection
            ref={sectionRef}
            gap="16px"
            data-automation={`account-signals-section-${signalType}`}
        >
            <Flex
                width="100%"
                justifyContent="space-between"
                alignItems="flex-start"
                gap="16px"
            >
                <Stack gap="8px">
                    <Flex
                        gap="8px"
                        alignItems="center"
                    >
                        <AccountSignalTypeIcon>
                            <SWReactIcons
                                iconName={icon}
                                iconColor={colorsPalettes.carbon[300]}
                                size="xs"
                            />
                        </AccountSignalTypeIcon>
                        <Text
                            fontVariant="h6"
                            color={colorsPalettes.carbon[400]}
                        >
                            {translate(signalsI18nKeys.signalType(signalType))}
                        </Text>
                    </Flex>
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[300]}
                    >
                        {translate(signalsI18nKeys.sectionDescription(signalType))}
                    </Text>
                </Stack>
                <Stack gap="4px">
                    {headerAction}
                    {meta}
                </Stack>
            </Flex>
            <Stack
                gap="12px"
                width="100%"
            >
                {isLoading ? (
                    <Stack
                        width="100%"
                        data-automation="account-signals-section-loader"
                        maxHeight="60px"
                    >
                        <TitlePlaceholderLoader />
                    </Stack>
                ) : isLocked && onRequestAccess ? (
                    <SignalUpsellBanner
                        signalType={signalType}
                        title={translate(signalsI18nKeys.lockedState.title(signalType))}
                        description={translate(signalsI18nKeys.lockedState.subtitle(signalType))}
                        ctaLabel={translate(signalsI18nKeys.lockedState.button(signalType))}
                        onRequestAccess={handleRequestAccess}
                        iconName={getIconForSignalType(signalType)}
                        isRequestSent={showsRequestSentState ? isAccessRequestSent : null}
                    />
                ) : signals.length === 0 ? (
                    <AccountSignalTypeSectionEmptyState
                        action={
                            areActionsEnabled ? (
                                <AddSignalButton
                                    signalType={signalType}
                                    onClick={handleAddSignalClick}
                                />
                            ) : undefined
                        }
                    />
                ) : (
                    <SignalsListItems
                        signals={signals}
                        selectedSignalType={signalType}
                        highlightedSignalId={highlightedSignalId}
                        isUserCustomizationAllowed
                        areActionsEnabled={areActionsEnabled}
                        onSignalCreate={onSignalCreate}
                        onSignalEdit={onSignalEdit}
                        onSignalDelete={onSignalDelete}
                    />
                )}
            </Stack>
        </StyledAccountSignalTypeSection>
    );
};
